// Ported from microsites/womens-conference/src/components/Hero.tsx. Changes:
// next/image -> img via art(), ReserveButton -> the widget's shared
// registration sheet (onRegister), the config arrives as a prop with MP's
// facts laid over it (lib/facts.ts), and the countdown reads the clock so the
// time machine moves it.
import {
  registrationOpen,
  type ConferenceConfig,
  type ConferencePhase,
  type SeatCount,
} from '../lib/conference';
import { datesLabel } from '../lib/format';
import { art } from '../lib/art';
import { Countdown } from './Countdown';
import { ShareInvite } from './ShareInvite';
import { fill, type Copy } from '../lib/copy';
import { VenueSheet } from './VenueSheet';

function ReserveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="wm-lift bg-wm-marigold text-wm-sage-950 inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold shadow-lg"
    >
      {label}
    </button>
  );
}

/**
 * The masthead knows what time it is AND who is looking.
 *
 * It is the key art rebuilt from its own parts rather than shown as one flat
 * export: the drawn "What Matters" wordmark is the h1, and the painted flowers
 * sit in the two bottom corners they were cut from. That means the title scales
 * with the page instead of being a fixed rectangle beside it, and the type stays
 * type rather than becoming pixels in a card.
 *
 * The one call to action follows the visitor's state, the flag-football way: not
 * registered means reserve a seat, registered means bring a friend. "My
 * conference" lives on the status card below, not here.
 */
export function Hero({
  config,
  now,
  shareUrl,
  onRegister,
  phase,
  isRegistered,
  copy,
  firstName,
  capacity,
  recap,
}: {
  config: ConferenceConfig;
  /** Wall clock now, real or the time machine's. */
  now: string;
  shareUrl: string;
  onRegister: () => void;
  phase: ConferencePhase;
  /** Seats taken and the cap, so a full conference stops offering one. */
  capacity?: SeatCount | null;
  /** Whether the signed-in visitor holds a registration. */
  isRegistered: boolean;
  /** Every word this masthead can say, already resolved. See lib/copy. */
  copy: Copy;
  /** Set when that visitor is signed in, so the hero can greet her by name. */
  firstName?: string | null;
  /** The team's own recap words, from `gathering_content`. Null until written. */
  recap?: { thanks: string | null; next: { label: string; url: string } | null } | null;
}) {
  const c = config;
  // Built from the day rows, not typed. This line said
  // "November 6 and 7, 2026" while `days` held the same two dates a few
  // lines away, so moving the conference moved everything except the one
  // sentence a visitor reads first.
  const dates = datesLabel(c.days, { year: true });
  const registrationUrl = shareUrl;
  const inviteTitle = `${c.name} Women's Conference`;

  return (
    <section className="bg-wm-sage-900 relative overflow-hidden">
      {/* The conference's actual watercolor ground (the comms team's own
          Background_V2 export), darkened just enough for cream type. */}
      <img
        src={art('/art/watercolor.png')}
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div aria-hidden className="bg-wm-sage-950/40 absolute inset-0" />
      {/* Bloom weather, blurred from the same painting shown crisp at right. */}
      <div aria-hidden className="absolute -top-40 -left-40">
        <div className="wm-bloom wm-bloom-blush" />
      </div>
      <div aria-hidden className="absolute -right-56 -bottom-64">
        <div className="wm-bloom wm-bloom-marigold" />
      </div>

      {/*
        Three columns, and the middle one is `auto`: the two gutters are `1fr`
        each, so they are always exactly equal and the content is centred by
        construction rather than by a margin someone has to keep true.

        The flowers live IN the gutters with `overflow-hidden`, which is the
        whole reason for the grid. They cannot reach the copy, the header links
        or the avatar, because they are clipped by a cell that does not contain
        them. On a narrow viewport the gutters shrink and the flowers quietly
        recede into the edges instead of colliding with anything.

        `pt-28` keeps the row itself below the header, so nothing in it can ever
        ride up under the logo. Height comes from the content cell's min-height,
        which the flowers then stretch to match: one number to tune, and the
        flowers follow it.
      */}
      {/* Not in the microsite: below lg the gutters shrink to almost
          nothing and clipped the flowers to slivers. There they sit whole in
          the bottom corners, sized to the screen, behind the words; from lg
          up they live in the gutters as the microsite drew them. */}
      <div className="relative grid grid-cols-[1fr_auto_1fr] pt-[max(4rem,calc(var(--ed-headroom,0px)+2rem))]">
        <div aria-hidden className="pointer-events-none relative lg:overflow-hidden">
          <img
            src={art('/art/flowers-left.png')}
            alt=""
            width={540}
            height={899}
            className="absolute bottom-0 left-0 h-auto w-[24vw] max-w-[13rem] lg:h-full lg:w-auto lg:max-w-none"
          />
        </div>

        <div className="relative z-10 flex min-h-[17rem] flex-col items-center justify-center px-6 pb-20 text-center sm:min-h-[19rem] md:min-h-[21rem] lg:min-h-[23rem]">
          {/* The title, drawn rather than set. Trimmed to its own ink, so
              centring the box centres the art: the export carried 88px of empty
              canvas on the left and 2px on the right, which read as the whole
              masthead sitting slightly right. */}
          <h1 className="w-full max-w-[19rem] sm:max-w-[26rem] lg:max-w-[28rem]">
            <img
              src={art('/art/what-matters-wordmark.png')}
              alt={`${c.name} — the Woodside Women's conference`}
              width={1108}
              height={508}
              className="h-auto w-full"
            />
          </h1>

          <div className="mt-5 flex w-full flex-col items-center">
            {/* The sales copy, the date, and the address are all answers to
              "should I come?" — a question nobody reading the recap still has.
              Once the weekend is over the masthead's job is to thank her and
              tell her what happened, so all three come out. */}
            {phase !== 'recap' ? (
              <>
                {/* The dot is the axis, not a bullet. The two halves are
                    different lengths, so centring the row as one block left the
                    separator wherever it happened to fall and the line read as
                    slightly off. Equal columns either side put the dot on the
                    page's centre line and let the text hang off it. Stacked
                    below sm, where there is nothing to balance. */}
                <div className="text-wm-cream flex flex-col items-center gap-y-1 text-sm font-medium sm:grid sm:grid-cols-[1fr_auto_1fr] sm:items-center sm:gap-x-5">
                  <span className="sm:justify-self-end">{dates}</span>
                  <span
                    aria-hidden
                    className="bg-wm-sage-500 hidden h-1 w-1 rounded-full sm:block"
                  />
                  {/* Not a link straight into Google Maps: that assumed she
                      wants directions, wants them now, and uses Google. The
                      sheet leads with the address for everyone who only wanted
                      to look. */}
                  <span className="sm:justify-self-start">
                    <VenueSheet venue={c.venue} />
                  </span>
                </div>
              </>
            ) : null}

            <div className="mt-6">
              {phase === 'registration' || phase === 'preEvent' ? (
                <div className="flex flex-col items-center gap-5">
                  {/* Two different deadlines, and which one the clock shows
                    depends on which one she can still miss. Selling seats: the
                    close. Sold out and waiting: the doors. With no
                    Registration_End in MP the close IS the doors, so it says
                    so (Colton, 2026-10-13). */}
                  {phase === 'registration' &&
                  c.registrationCloses.slice(0, 19) !== c.eventStarts.slice(0, 19) ? (
                    <Countdown
                      now={now}
                      target={c.registrationCloses}
                      caption="Registration closes in"
                      startedLabel="Registration is closed."
                    />
                  ) : (
                    <Countdown
                      now={now}
                      target={c.eventStarts}
                      caption="Doors open in"
                      startedLabel="It has begun."
                    />
                  )}
                  {/* Being signed up is not the end of what she might want to
                      do here. Sitting behind this button for weeks was the
                      ordinary case of coming back in October to sign up a wife
                      or a sister — the sheet could always do it, and nothing
                      on the page said so. So while there are places to be had,
                      the first thing she is offered is signing somebody else
                      up; the share link steps back rather than competing. */}
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    {registrationOpen(phase, capacity) ? (
                      <ReserveButton
                        label={copy[isRegistered ? 'register.ctaAgain' : 'register.cta']}
                        onClick={onRegister}
                      />
                    ) : null}
                    {isRegistered ? (
                      <ShareInvite
                        url={registrationUrl}
                        title={inviteTitle}
                        tone={registrationOpen(phase, capacity) ? 'secondary' : 'primary'}
                      />
                    ) : null}
                  </div>
                </div>
              ) : null}

              {phase === 'live' ? (
                <div className="flex flex-col gap-5">
                  <p className="text-wm-cream text-lg">
                    <span
                      className="bg-wm-marigold mr-2 inline-block h-2.5 w-2.5 animate-pulse rounded-full align-middle"
                      aria-hidden
                    />
                    {fill(copy['hero.live'], { name: firstName || 'friend' })}
                  </p>
                  <a
                    href="#schedule"
                    className="wm-lift bg-wm-marigold text-wm-sage-950 inline-flex w-fit items-center justify-center rounded-full px-6 py-3 text-sm font-semibold shadow-lg"
                  >
                    {copy['hero.liveCta']}
                  </a>
                </div>
              ) : null}

              {phase === 'recap' ? (
                // Centred on the masthead's axis like every other phase. Without
                // items-center the column stretches, so the max-w-xl thanks and
                // the button both hung off the left edge while the text inside
                // them centred — which read as a layout bug rather than a
                // choice.
                <div className="flex flex-col items-center gap-5">
                  {/* She came. Lead with that, by name, and then tell her what
                    the weekend actually was — written by the team afterward,
                    not assembled from counts. */}
                  <p className="wm-display text-wm-cream text-4xl italic sm:text-5xl">
                    {isRegistered
                      ? `Thank you for coming${firstName ? `, ${firstName}` : ''}.`
                      : 'What a weekend.'}
                  </p>
                  {/* The team's own words, from `gathering_content`. Read off
                      the config until now, where the placeholder describes
                      lines at the prayer tables and notebooks full by Saturday
                      afternoon — an eyewitness account of a weekend nobody has
                      lived through. Nothing written is better than something
                      invented, so an unwritten recap simply has no paragraph. */}
                  {recap?.thanks ? (
                    <p className="text-wm-sage-200 max-w-xl text-base">{recap.thanks}</p>
                  ) : null}
                  {/* One button, and it points off this page. "Relive it" asked
                    her to scroll down on a page she is already on; the only
                    thing she can actually do next is the next thing the church
                    is doing. Which season that is comes from the config. */}
                  {recap?.next ? (
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <a
                        href={recap.next.url}
                        className="wm-lift bg-wm-marigold text-wm-sage-950 inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold shadow-lg"
                      >
                        {recap.next.label}
                      </a>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div aria-hidden className="pointer-events-none relative lg:overflow-hidden">
          <img
            src={art('/art/flowers-right.png')}
            alt=""
            width={416}
            height={816}
            className="absolute right-0 bottom-0 h-auto w-[19vw] max-w-[10rem] lg:h-full lg:w-auto lg:max-w-none"
          />
        </div>
      </div>
    </section>
  );
}
